// Single source of truth for executing a console command in the APP tab.
// Console code never calls flows directly; it names an action, this maps it.

import { isWithAgent, slotBookingId, studioDayKey, studioLocalIso, studioWallTimeToIso } from '@atink/domain';
import { simulationApi } from '@/events/simulationEngine';
import { SCRIPTED_EVENTS } from '@/data/scriptedEvents';
import type { SimEventType } from '@/events/simEvents';
import { runDayAdvanced } from '@/domain/simClockFlow';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { canApproveNoDepositOverride } from '@/domain/permissions';
import { runThresholdAlerts } from '@/domain/alertsFlow';
import {
  advanceSequenceClock,
  fireScheduledCallback,
  markSaidNo,
  pushToQueue,
  recordContactAttempt,
  simulateDepositPaid,
  simulateInboundReply,
} from '@/domain/sequenceFlow';
import {
  checkInBooking,
  checkOutBooking,
  completeCheckout,
  recordArtistProgressPhotos,
  recordArtistSessionNote,
  seatBooking,
  type CheckOutInput,
} from '@/domain/sessionFlow';
import { bookingPriceAud } from '@/domain/pricing';
import { currentStaffAgentId } from '@/domain/actor';
import { depositPaidAud } from '@/utils/bookingSummary';
import {
  ensureConfirmationCall,
  markNoShow,
  resolveConfirmationOutcome,
  type ConfirmationOutcome,
} from '@/domain/confirmationFlow';
import { askArtistToConfirm } from '@/domain/confirmationEscalationFlow';
import { isConfirmationEscalated } from '@/domain/confirmationWorklist';
import {
  artistOutstanding,
  deferPayout,
  payableBundle,
  recordFloatMovement,
  sendPayouts,
  settlePayouts,
} from '@/domain/payoutFlow';
import { dayKey } from '@/utils/studioDay';
import { closeDay, expectedTillAud, reopenDay, unsignedPayoutArtists } from '@/domain/dayCloseFlow';
import {
  forfeitDepositForNoShow,
  generateDepositLink,
  generateSelfBookLink,
  isEffectivelyExpired,
  placeBookingFromDeposit,
  reinstateDepositWithReason,
  recordManualPayment,
  resendDepositLink,
  simulateDepositLinkViewed,
  simulateStripeWebhookPaid,
} from '@/domain/depositFlow';
import { routeExpiredResend } from '@/domain/resendRoutingFlow';
import { reconcileDay, reconciliationDaysFor, orphanLane } from '@/domain/reconciliationFlow';
import {
  approveReconResolution,
  rejectReconResolution,
  resolveReconRow,
} from '@/domain/reconResolutionFlow';
import { landPendingBatches } from '@/domain/acquirerSim';
import { canResolveReconException } from '@/domain/permissions';
import { latestWaiverForBooking, sendWaiver, simulateWaiverSigned } from '@/domain/waiverFlow';
import { rescheduleBooking } from '@/domain/rescheduleFlow';
import {
  listPendingOverrideRequests,
  requestNoDepositOverride,
  resolveNoDepositOverride,
  waiveDepositDirectly,
} from '@/domain/approvals';
import { opsCancelBooking } from '@/domain/opsCancelFlow';
import { opsExtendPayBy } from '@/domain/opsExtendFlow';
import { scheduleDayOff } from '@/domain/artistFlow';
import {
  answerInboundCall,
  declineInboundCall,
  dispatchInboundCall,
  endCall,
  getActiveCall,
  startOutboundCall,
} from '@/domain/callFlow';
import {
  completeUniversalSelfBook,
  getOrCreateUniversalLink,
  reconcileSelfBookLinkExpiry,
} from '@/domain/selfBookFlow';
import { routeOverbookRecall } from '@/domain/resendRoutingFlow';
import { seatWalkInNow, bookWalkInFuture } from '@/domain/walkInFlow';
import { sendTopupReminder } from '@/domain/topupReminders';
import { currentActorId } from '@/domain/actor';
import { topupChaseNoAnswer } from '@/domain/topupChaseFlow';
import { nurtureInbound, reconcileNurture } from '@/domain/nurtureSeam';
import { createTopupPlan, payTopupInstalment, MIN_INSTALMENT_AUD } from '@/domain/topupBuilderFlow';
import { seedInboundDm } from '@/domain/inboundDm';
import { customerName } from '@/utils/format';
import type { Booking, Deposit, FloatMovement, StudioId } from '@/domain/types';
import { timeOnSimDay } from '@/utils/slotClip';
import { recordSweeperOutcome } from '@/domain/sweeperFlow';

/** A real tattoo frame from the seed pool, so a console-captured progress photo
 *  looks like the rest of the imagery rather than a broken tile. */
const PROGRESS_PHOTO_URL = 'https://images.unsplash.com/photo-1611501275019-9b5cda994e8d?w=720&q=75';


type Payload = Record<string, unknown> | undefined;

function str(p: Payload, key: string): string | undefined {
  const v = p?.[key];
  return typeof v === 'string' ? v : undefined;
}
function num(p: Payload, key: string): number | undefined {
  const v = p?.[key];
  return typeof v === 'number' ? v : undefined;
}
function bool(p: Payload, key: string): boolean | undefined {
  const v = p?.[key];
  return typeof v === 'boolean' ? v : undefined;
}

/** "Now" is always the sim clock, never Date.now(). */
function nowIso(): string {
  return useSimulationStore.getState().clockIso;
}
/** The sim clock's studio (Melbourne) day, whatever form the clock string takes (7fra). */
function today(): string {
  return studioDayKey(nowIso());
}
function tomorrow(): string {
  const [y, m, d] = today().split('-').map(Number);
  return new Date(Date.UTC(y!, m! - 1, d! + 1)).toISOString().slice(0, 10);
}
/** 11:00 Melbourne tomorrow, as a studio-local string with its real offset. */
function tomorrowAt11(): string {
  return studioLocalIso(Date.parse(studioWallTimeToIso(tomorrow(), '11:00')));
}
/** The studio the command names, else the first one on the books. */
function resolveStudio(p: Payload): StudioId | undefined {
  const brain = useBrainStore.getState();
  return (str(p, 'studioId') ?? Object.keys(brain.studios)[0]) as StudioId | undefined;
}

// --- Aiming -----------------------------------------------------------------
// Every handler takes its target through one of these. When the console has
// aimed at a specific record its id arrives on the payload and wins outright;
// otherwise the handler falls back to the first record matching what it needs.
// An aimed id that does NOT satisfy the handler's own requirement is still
// honoured — the operator asked for that record, so acting on it (or failing
// visibly) beats silently working on somebody else's.

function pickBooking(p: Payload, fits: (b: Booking) => boolean): Booking | undefined {
  const brain = useBrainStore.getState();
  const aimed = str(p, 'bookingId');
  if (aimed) return brain.bookings[aimed];
  return Object.values(brain.bookings).find(fits);
}

function pickDeposit(p: Payload, fits: (d: Deposit) => boolean): Deposit | undefined {
  const brain = useBrainStore.getState();
  const aimed = str(p, 'depositId');
  if (aimed) return brain.deposits[aimed];
  return Object.values(brain.deposits).find(fits);
}

function pickCustomerId(p: Payload): string | undefined {
  const brain = useBrainStore.getState();
  const aimed = str(p, 'customerId');
  if (aimed) return brain.customers[aimed]?.id;
  // Prefer the money cohort (deposit on file, no date) — the queue's real work.
  const entries = Object.entries(brain.sequenceState ?? {});
  const target = entries.find(([, s]) => s.cohort === 'money') ?? entries[0];
  return target?.[0] ?? Object.keys(brain.customers)[0];
}

function pickArtistId(p: Payload, studioId?: StudioId): string | undefined {
  const brain = useBrainStore.getState();
  const aimed = str(p, 'artistId');
  if (aimed) return brain.artists[aimed]?.id;
  const inStudio = Object.values(brain.artists).find((a) => !studioId || a.studioId === studioId);
  return inStudio?.id ?? Object.keys(brain.artists)[0];
}

/**
 * L5 (tv-54xv): the console's checkouts go through the same ONE server checkout
 * as the Floor (booking, sale and till cash together, idempotent), not the old
 * client cascade. The offline store-fallback build runs it synchronously.
 */
function consoleCheckOut(input: CheckOutInput): void {
  if (isStoreFallbackEnabled()) {
    checkOutBooking(input);
    return;
  }
  void completeCheckout(input).then((out) => {
    if (!out.ok) console.warn('[console] checkout refused', out.kind, out.message);
  });
}

/** A plain full-price checkout: the booking's own price less whatever the
 *  deposit already covered, taken on card. Mirrors what the floor screen sends
 *  (`FloorBooking.tsx` balanceDueAud) so console checkouts and hand-driven ones
 *  land the same numbers in the money loop. */
function checkOutFor(booking: Booking): CheckOutInput {
  const brain = useBrainStore.getState();
  const price = bookingPriceAud(booking, brain.artists[booking.artistId]);
  const deposit = booking.depositId ? brain.deposits[booking.depositId] : undefined;
  const applied = depositPaidAud(deposit);
  return {
    bookingId: booking.id,
    paymentMethod: 'card',
    amountPaidAud: Math.max(0, price - applied),
    depositAppliedAud: applied,
    rolloverDepositToNextBooking: false,
    sessionNote: 'Console: session wrapped up.',
    // tv-ona: the console drives the signed-in persona's session, so the
    // checkout is theirs (agents only: bookings.checked_out_by -> app.agents).
    ...(currentStaffAgentId() ? { checkedOutBy: currentStaffAgentId() } : {}),
  };
}

/** Shift an ISO instant, emitting a Melbourne-local string (its real offset,
 *  +10:00 or +11:00 in daylight saving; 7fra) so the sim clock stays canonical
 *  and finance day-matching never drifts. */
function shiftIsoByMinutes(iso: string, minutes: number): string {
  return studioLocalIso(new Date(iso).getTime() + minutes * 60_000);
}

/**
 * What a command did. A handler returning `false` means it found nothing to act
 * on; a `note` explains a refusal the operator would otherwise experience as
 * "I pressed it and nothing happened" — the underlying flow's own gate (an
 * unsigned waiver, a self-approval block) rather than a missing target.
 */
export interface ControlResult {
  ok: boolean;
  note?: string;
}

/** Set by a handler to explain the outcome of the command it just ran. */
let lastNote: string | undefined;
function refuse(note: string): boolean {
  lastNote = note;
  return false;
}

/** The day's alert-surfacing pass: fire a channel ping for each breached threshold
 *  band so the queue Alerts tab lights up (dedup per breach per sim day lives in
 *  runThresholdAlerts). Shared by `global:rollover` — which advances the clock
 *  first — and the `agent-batch-alerts` demo beat, which surfaces the current
 *  day's alerts without moving time. Returns how many pings were posted. */
function runDailyAlertPass(iso: string = nowIso()): number {
  return runThresholdAlerts(iso);
}

/** Move the sim clock the way the facilitator's day stepper does: the store's
 *  setClock (persisted to the shared localStorage key by the store) and today's
 *  stats re-derived. `simulationApi.setClock` is a deliberate no-op, so the
 *  console cards must not go through it (tv-2na.10.7). Moving back is allowed;
 *  the returned note says so. */
function moveSimClock(iso: string): ControlResult {
  const before = Date.parse(useSimulationStore.getState().clockIso);
  useSimulationStore.getState().setClock(iso);
  useBrainStore.getState().recomputeTodayStats();
  if (Date.parse(iso) < before) return { ok: true, note: 'Clock moved back.' };
  // A forward move runs the ONE day-advanced bundle (tv-2na.10.13).
  if (Date.parse(iso) > before) void runDayAdvanced();
  return { ok: true };
}

// Flow handlers. Each resolves a live target from the store so the catalogue
// stays free of fragile seed ids. A handler returns false when it found nothing
// to act on (so the caller can report a friendly no-op).

/**
 * B3: on the served build the server decides overrides as the signed-in person and
 * refuses anyone but Ops, so the console only fires those cards for an Ops seat
 * (no false "done" followed by a revert).
 */
function consoleCanDecideOverrides(): boolean {
  if (isStoreFallbackEnabled()) return true;
  const persona = useSessionStore.getState().persona;
  return !!persona && canApproveNoDepositOverride(persona);
}

/**
 * tv-toef: on the served build the server extends a pay-by (and resets the
 * chase) only for Ops or the owner, so the console fires that card only for
 * those seats (same as the reinstate intent's REINSTATERS).
 */
function consoleCanExtendPayBy(): boolean {
  if (isStoreFallbackEnabled()) return true;
  const persona = useSessionStore.getState().persona;
  return persona === 'georgina' || persona === 'owner';
}

const FLOW_HANDLERS: Record<string, (p: Payload) => boolean> = {
  // QUEUE — a dateless-cohort customer replies to a nudge. `sequenceState` is
  // keyed BY customerId (the state values themselves carry no id), so the
  // target customer must come from the record key, not the value. Prefer the
  // 'money' cohort (deposit-on-file, no booking date - the "dateless" cohort);
  // fall back to any queued customer so the action still does something on
  // seeds that have no money-cohort entry.
  'queue-inbound-reply': (p) => {
    const customerId = pickCustomerId(p);
    if (!customerId) return false;
    // `recordInboundReply` only re-arms an EXISTING sequence, so a reply from
    // someone not yet handed off would land as a message nobody sees. Push them
    // in first: a client replying is exactly what should surface them. Key on a
    // GENUINE push (sourceConversationId), not bare record existence, so a
    // merely-contacted lead gets pushed too (pushToQueue patches it in place).
    const b = useBrainStore.getState();
    if (!isWithAgent(b.customers[customerId], b.sequenceState[customerId])) {
      pushToQueue({ customerId, description: 'Console: replied to a nudge' });
    }
    simulateInboundReply(customerId);
    return true;
  },

  // CONVERSATIONS — an overseas messenger hands a phone-numbered lead to the
  // studio's agent queue. A push IS a sequence record carrying a
  // sourceConversationId (buildStandardQueue keys the pushed-lead card on it), so
  // the target must be a lead NOT already pushed — re-pushing an in-flight one is
  // a no-op the operator would read as a dud button. Honour an aimed customer;
  // otherwise take the first lead with no pushed sequence.
  'messenger-push-to-queue': (p) => {
    const brain = useBrainStore.getState();
    const aimed = str(p, 'customerId');
    const customerId = aimed
      ? brain.customers[aimed]?.id
      : Object.keys(brain.customers).find(
          (id) => !isWithAgent(brain.customers[id], brain.sequenceState[id]),
        );
    if (!customerId) return refuse('No lead available to push to the queue.');
    const pushed = pushToQueue({
      customerId,
      description: 'Console: overseas messenger handed off a phone-numbered lead',
    });
    return pushed ? true : refuse('That lead is already in the agent queue.');
  },

  // CONVERSATIONS — a first inbound Instagram DM arrives from a handle that may
  // or may not already be a customer. Create-or-match on the normalized handle
  // (strong-ID), then drop an inbound ig-dm message so the thread surfaces in
  // the Messenger inbox (studio-scoped, channel-filtered). A repeat handle
  // matches the existing customer instead of minting a duplicate.
  'seed-inbound-dm': (p) => {
    const brain = useBrainStore.getState();
    const studioId = (str(p, 'studioId') || Object.keys(brain.studios)[0]) as StudioId | undefined;
    if (!studioId) return refuse('No studio available to attach the DM to.');
    seedInboundDm({ handle: str(p, 'instagramHandle'), studioId });
    return true;
  },

  // QUEUE — the agent clears their batch and the day's alerts surface on the
  // Alerts tab. Runs the same alert-surfacing pass as `global:rollover`
  // (runDailyAlertPass) but on the current clock: this beat surfaces alerts, it
  // does not move time.
  'agent-batch-alerts': () => {
    runDailyAlertPass();
    return true;
  },

  // FLOOR — check in the next booking that is due but not yet checked in.
  'floor-check-in': (p) => {
    const target = pickBooking(p, (b) => b.status === 'confirmed' && !b.checkedInAt);
    if (!target) return false;
    const result = checkInBooking(target.id);
    if (!result.ok) {
      return refuse(
        result.reason === 'waiver-unsigned'
          ? 'Waiver not signed — send + sign it first (Links + waivers).'
          : `Check-in refused: ${result.reason}.`,
      );
    }
    return true;
  },

  // FLOOR — a walk-in takes a gap NOW, no deposit: seats a confirmed booking that
  // checks out and pays in full afterwards (Diagram 07, tv-pq6). Aims at an
  // artist, else the first on the books; a fresh walk-in customer is minted.
  'walkin-seat-now': (p) => {
    const artistId = pickArtistId(p, resolveStudio(p));
    if (!artistId) return false;
    const customerId = str(p, 'customerId');
    seatWalkInNow({
      artistId,
      startIso: nowIso(),
      durationMin: num(p, 'durationMin') ?? 120,
      ...(customerId ? { customerId } : { name: str(p, 'name') ?? 'Walk-in' }),
    });
    return true;
  },

  // FLOOR — no gap today: the walk-in books a future slot and the deposit is taken
  // in the shop now (EFTPOS), locking a confirmed future booking (tv-pq6).
  'walkin-book-future': (p) => {
    const artistId = pickArtistId(p, resolveStudio(p));
    if (!artistId) return false;
    const customerId = str(p, 'customerId');
    const res = bookWalkInFuture({
      artistId,
      startIso: shiftIsoByMinutes(nowIso(), 24 * 60),
      durationMin: num(p, 'durationMin') ?? 120,
      method: 'eftpos',
      // D05-11: a deposit taken now needs an email; the console's made-up walk-in
      // gets a reserved example.com address unless the payload names one.
      ...(customerId
        ? { customerId, ...(str(p, 'email') ? { email: str(p, 'email') } : {}) }
        : { name: str(p, 'name') ?? 'Walk-in', email: str(p, 'email') ?? `walk-in.${Date.now().toString(36)}@example.com` }),
    });
    return !!res.bookingId;
  },

  // QUEUE — pull a customer's future-readiness callback forward so it fires now.
  // Signals live ON the customer record, so the target comes from a scan of
  // customers rather than a top-level collection.
  'queue-scheduled-callback': () => {
    const brain = useBrainStore.getState();
    for (const customer of Object.values(brain.customers)) {
      const signal = customer.futureReadinessSignals.find(
        (s) => s.status === 'scheduled' || s.status === 'snoozed',
      );
      if (!signal) continue;
      fireScheduledCallback(signal.id);
      return true;
    }
    return false;
  },

  // QUEUE — an expired link is resent. Routing decides the outcome: the original
  // slot still free re-mints the link, a taken slot pushes the customer to the top
  // of the money band instead of guessing a new date.
  'queue-expired-resend': (p) => {
    const expired = pickDeposit(p, isEffectivelyExpired);
    if (!expired) return false;
    return routeExpiredResend(expired.id) !== undefined;
  },

  // QUEUE — age the dateless cohort so nudge windows come due. Advances the sim
  // clock (default a full day) and runs the sequence / deposit / waiver sweeps.
  'queue-age-cohort': (p) => {
    advanceSequenceClock(num(p, 'hours') ?? 24);
    return true;
  },

  // QUEUE — the customer says no; the sequence terminates red.
  'queue-said-no': (p) => {
    const customerId = pickCustomerId(p);
    if (!customerId) return false;
    markSaidNo(customerId);
    return true;
  },

  // QUEUE — a cohort customer pays: mints a fresh link for them and settles it.
  'queue-deposit-paid': (p) => {
    const customerId = pickCustomerId(p);
    if (!customerId) return false;
    simulateDepositPaid(customerId);
    return true;
  },

  // FLOOR — seat the first checked-in booking (the chair fills).
  'floor-seat': (p) => {
    const target = pickBooking(p, (b) => b.status === 'checked-in');
    if (!target) return false;
    const result = seatBooking(target.id, num(p, 'boothNumber'));
    if (!result.ok) return refuse(`Cannot seat: ${result.reason}.`);
    return true;
  },

  // FLOOR — the next due booking never turns up. Flips it to no-show, which also
  // runs the forfeit path on any paid deposit.
  'floor-no-show': (p) => {
    const target = pickBooking(p, (b) => b.status === 'confirmed' && !b.checkedInAt);
    if (!target) return false;
    markNoShow(target.id, { by: 'Console', note: 'Console: no-show' });
    return true;
  },

  // FLOOR — make a booking read as running late. Lateness is derived from the
  // clock (`lateness.isRunningLate`), so the trigger moves the clock past the
  // booking's start rather than writing a flag.
  'floor-late-arrival': (p) => {
    const target = pickBooking(
      p,
      (b) => (b.status === 'confirmed' || b.status === 'checked-in') && !b.seatedAt,
    );
    if (!target) return false;
    const minutesLate = num(p, 'minutesLate') ?? 20;
    // Move the BOOKING back behind the clock, never the clock forward past the
    // booking. Lateness is derived (`isRunningLate(start, now)`), so shoving the
    // world clock forward to make ONE client late makes every earlier
    // un-arrived booking late too — and drags every other clock-derived rule
    // (deposit expiry, payout clock-off, settlement timing, queue urgency)
    // along with it. One client should be one client.
    useBrainStore
      .getState()
      .upsertBooking({ ...target, start: shiftIsoByMinutes(nowIso(), -minutesLate) });
    return true;
  },

  // PAYOUTS + CASH — record a float withdrawal (default $200) for a studio.
  // `amountAud` is a SIGNED delta: cash LEAVING the drawer is negative, matching
  // PayoutCashPanel's `direction * amount` with direction = -1 for a withdrawal.
  'cash-float-withdrawal': (p) => {
    const studioId = resolveStudio(p);
    if (!studioId) return false;
    const dayIso = today();
    const kind: FloatMovement['kind'] = 'withdrawal';
    const magnitude = num(p, 'amountAud') ?? 200;
    recordFloatMovement(studioId, dayIso, kind, -magnitude, 'Console: float withdrawal');
    return true;
  },

  // PAYOUTS + CASH — close today's day for a studio short by $40.
  'cash-close-day-short': (p) => {
    const studioId = resolveStudio(p);
    if (!studioId) return false;
    const dayIso = today();
    const expected = expectedTillAud(studioId, dayIso);
    closeDay({ studioId, dayIso, countedAud: Math.max(0, expected - 40), closedBy: 'Console' });
    return true;
  },

  // PAYOUTS + CASH — top the drawer up (default $200). Signed positive, the
  // mirror of the withdrawal above.
  'cash-float-infusion': (p) => {
    const studioId = resolveStudio(p);
    if (!studioId) return false;
    const dayIso = today();
    const kind: FloatMovement['kind'] = 'infusion';
    recordFloatMovement(studioId, dayIso, kind, num(p, 'amountAud') ?? 200, 'Console: float infusion');
    return true;
  },

  // FLOOR — the session in the chair wraps up: checks out at the booking's own
  // price, less whatever the deposit already covered, paid by card. This is the
  // trigger that writes a SalePayment, so it is what feeds the money loop.
  'floor-check-out': (p) => {
    const target = pickBooking(p, (b) => b.status === 'in-chair');
    if (!target) return false;
    consoleCheckOut(checkOutFor(target));
    return true;
  },

  // FLOOR: a mid-piece client wraps up but does NOT rebook (tv-3gl). Checks the
  // chair out on the decline path with a typed reason, leaving the piece
  // unfinished: that raises the Ops flag (mandatory reason) AND drops the client
  // onto the Sweeper's open-business rung. Seats a checked-in target first.
  'floor-no-rebook-flag': (p) => {
    const brain = useBrainStore.getState();
    const midPiece = (b: Booking): boolean => {
      if (!b.pieceId) return false;
      const piece = brain.pieces[b.pieceId];
      return !piece || piece.status === 'in-progress';
    };
    const target = pickBooking(
      p,
      (b) => (b.status === 'in-chair' || b.status === 'checked-in') && midPiece(b),
    );
    if (!target) return refuse('No mid-piece client in the chair to flag.');
    if (target.status === 'checked-in') seatBooking(target.id);
    consoleCheckOut({
      ...checkOutFor(target),
      markPieceComplete: false,
      rebookCapture: {
        outcome: {
          kind: 'noRebookDepositApplied',
          reason: 'Not ready to book the next sitting',
        },
        attribution: {
          facilitatedBy: 'floorManager',
          facilitatedByStaffId: 'console',
          capturedAt: nowIso(),
        },
      },
    });
    return true;
  },

  // PAYOUTS + CASH — clear one artist's floor so their envelope can be signed.
  // An envelope is payable only once EVERY session of theirs is completed, and
  // the demo day deliberately leaves people in the chair, so signing needs this
  // first. Checks out every open session for the artist with the fullest chair.
  'payout-make-payable': (p) => {
    const brain = useBrainStore.getState();
    const studioId = resolveStudio(p);
    if (!studioId) return false;
    const open = Object.values(brain.bookings).filter(
      (b) =>
        b.studioId === studioId &&
        studioDayKey(b.start) === today() &&
        (b.status === 'in-chair' || b.status === 'checked-in'),
    );
    const artistId = str(p, 'artistId') ?? open[0]?.artistId;
    if (!artistId) return false;
    const theirs = open.filter((b) => b.artistId === artistId);
    if (theirs.length === 0) return false;
    for (const booking of theirs) {
      // Seat anyone still at the desk first — checkout runs from the chair.
      if (booking.status === 'checked-in') seatBooking(booking.id);
      consoleCheckOut(checkOutFor(booking));
    }
    return true;
  },

  // PAYOUTS + CASH — settle the first signable envelope, together with anything
  // that artist is still owed from earlier days (oldest first). An artist is never
  // paid in part, so a drawer that cannot cover a whole day either breaks the float
  // (when the trigger asks for it) or defers that day intact — never a half payment.
  'payout-sign': (p) => {
    const studioId = resolveStudio(p);
    if (!studioId) return false;
    const dayIso = today();
    const target = unsignedPayoutArtists(studioId, dayIso)[0];
    if (!target) return false;
    if (str(p, 'shortfall') === 'float-dip') {
      // L5 (tv-mg91): breaking the float funds the envelope; the artist still
      // counts and signs it on their own device (only their hold signs it).
      return sendPayouts({
        studioId,
        artistId: target.artistId,
        dayIsos: [dayIso],
        payDayIso: dayIso,
        breakFloatBy: 'Console',
      }).ok;
    }
    // Whole days the drawer covers go out now; the rest rolls, so the trigger
    // still lands rather than failing outright on a thin till.
    const bundle = payableBundle(studioId, target.artistId, dayIso);
    const settled =
      bundle.dayIsos.length > 0 &&
      settlePayouts({
        studioId,
        artistId: target.artistId,
        dayIsos: bundle.dayIsos,
        payDayIso: dayIso,
        signedByName: 'Console',
      }).ok;
    const deferred = bundle.blockedDayIsos.some(
      (day) => deferPayout({ studioId, artistId: target.artistId, dayIso: day, decidedBy: 'Console' }).ok,
    );
    return settled || deferred;
  },

  // PAYOUTS + CASH — hand the first payable envelope to the artist to confirm,
  // together with anything they are still owed from earlier days, so the artist's
  // Earnings tab shows the bundle it will actually be asked to sign for.
  'payout-send': (p) => {
    const studioId = resolveStudio(p);
    if (!studioId) return false;
    const dayIso = today();
    const target = unsignedPayoutArtists(studioId, dayIso)[0];
    if (!target) return false;
    const arrears = artistOutstanding(studioId, target.artistId, dayIso).map((d) => d.dayIso);
    return sendPayouts({
      studioId,
      artistId: target.artistId,
      dayIsos: [...arrears, dayKey(dayIso)],
      payDayIso: dayIso,
    }).ok;
  },

  // PAYOUTS + CASH — reopen the closed day (the frozen pack goes back to live).
  'cash-reopen-day': (p) => {
    const studioId = resolveStudio(p);
    if (!studioId) return false;
    const pack = reopenDay({
      studioId,
      dayIso: today(),
      reopenedBy: 'Console',
      reason: 'Console: reopened for the demo',
    });
    return pack !== undefined;
  },

  // CALENDAR — land a paid, dateless deposit on a slot. Prefers the slot that was
  // discussed on the call; otherwise puts it on tomorrow at 11am.
  'calendar-place-booking': (p) => {
    const deposit = pickDeposit(p, (d) => d.status === 'paid' && !d.bookingId);
    if (!deposit) return false;
    const artistId = str(p, 'artistId') ?? deposit.artistId ?? pickArtistId(p);
    if (!artistId) return false;
    const startIso =
      str(p, 'startIso') ??
      deposit.discussedSlot?.startIso ??
      tomorrowAt11();
    placeBookingFromDeposit({ depositId: deposit.id, artistId, startIso });
    return true;
  },

  // CALENDAR — move the next future booking out a week. Keep-original: the
  // source booking is superseded, not edited.
  'calendar-reschedule': (p) => {
    const nowMs = new Date(nowIso()).getTime();
    const target = pickBooking(
      p,
      (b) => b.status === 'confirmed' && new Date(b.start).getTime() >= nowMs,
    );
    if (!target) return false;
    return rescheduleBooking({
      bookingId: target.id,
      reason: 'client-move',
      pathway: {
        mode: 'move',
        artistId: target.artistId,
        newStartIso: shiftIsoByMinutes(target.start, (num(p, 'days') ?? 7) * 24 * 60),
      },
      by: 'Console',
    }).ok;
  },

  // CALENDAR — a booking with no deposit needs sign-off.
  'calendar-override-request': (p) => {
    const target = pickBooking(p, (b) => !b.depositId);
    if (!target) return false;
    return (
      requestNoDepositOverride({
        bookingId: target.id,
        requestedBy: 'Console',
        note: 'Console: no deposit on file',
      }) !== undefined
    );
  },

  // CALENDAR — the oldest pending override is resolved (approved by default).
  'calendar-override-resolve': (p) => {
    if (!consoleCanDecideOverrides()) return false;
    const brain = useBrainStore.getState();
    const request = listPendingOverrideRequests(brain.overrideRequests)[0];
    if (!request) return false;
    return (
      resolveNoDepositOverride({
        requestId: request.id,
        approve: bool(p, 'approve') ?? true,
        // Decided as the signed-in person, as the server records it (B3); the
        // console raised it as 'Console', so it is never a self-approval.
      }) !== undefined
    );
  },

  // OPS - decline the oldest pending no-deposit override. Rejects the request AND
  // sets the customer's decline flag (inside resolveNoDepositOverride), so the Agent can no
  // longer make a no-money booking for them and must chase a real deposit. This is
  // the ONLY console writer of the decline flag (converts its coverage excuse to a
  // real card).
  'ops-override-decline': (p) => {
    if (!consoleCanDecideOverrides()) return false;
    const brain = useBrainStore.getState();
    const aimed = str(p, 'requestId');
    const request = aimed
      ? brain.overrideRequests[aimed]
      : listPendingOverrideRequests(brain.overrideRequests)[0];
    if (!request || request.status !== 'pending') return false;
    const resolved = resolveNoDepositOverride({
      requestId: request.id,
      approve: false,
      resolutionNote: 'Console: declined - collect a deposit.',
    });
    // The decline itself sets the customer's flag and the hold's pay-by link (B3).
    return resolved !== undefined;
  },

  // OPS - cancel a live booking with a typed reason (Ops-only). Frees the slot to
  // the Sweeper and forks the deposit on the 7-day line (inside forfeits, outside
  // keeps on file). Aims via bookingId, else the next live booking.
  'ops-cancel-booking': (p) => {
    const target = pickBooking(
      p,
      (b) => b.status === 'confirmed' || b.status === 'held' || b.status === 'pending-deposit',
    );
    if (!target) return false;
    return opsCancelBooking({
      bookingId: target.id,
      reason: str(p, 'reason') ?? 'Console: Ops cancel',
      by: 'Console',
    }).ok;
  },

  // OPS - extend a top-up / deposit pay-by date, re-pointing the reminder to the
  // new date. Aims via planId, else the first active plan with an unsettled
  // instalment; pushes that instalment's due date out a fortnight.
  'ops-extend-payby': (p) => {
    if (!consoleCanExtendPayBy()) return refuse('Sign in as Ops (Georgina) or the owner to extend a pay-by date.');
    const brain = useBrainStore.getState();
    const aimed = str(p, 'planId');
    const plan = aimed
      ? brain.topupPlans[aimed]
      : Object.values(brain.topupPlans).find(
          (pl) =>
            pl.status === 'active' &&
            pl.instalments.some(
              (i) => i.status === 'scheduled' || i.status === 'reminded' || i.status === 'missed',
            ),
        );
    if (!plan) return false;
    const current = plan.instalments.find(
      (i) => i.status !== 'paid' && i.status !== 'cancelled',
    );
    if (!current) return false;
    const days = num(p, 'days') ?? 14;
    return opsExtendPayBy({
      planId: plan.id,
      newDueIso: shiftIsoByMinutes(current.dueIso, days * 24 * 60),
      by: 'Console',
    }).ok;
  },

  // CALENDAR — an artist takes tomorrow off, so the grid loses a column.
  'calendar-day-off': (p) => {
    const artistId = pickArtistId(p);
    if (!artistId) return false;
    return (
      scheduleDayOff(artistId, {
        kind: 'rdo',
        date: tomorrow(),
        note: 'Console: day off',
        confirmedBy: 'Console',
      }) !== null
    );
  },

  // DEPOSITS — mark the first pending deposit paid (Stripe webhook).
  'deposit-stripe-paid': (p) => {
    // Only a dated link can be paid (7g3y: a link always carries a date).
    // D05-11: money is only taken with the client's email on file.
    const customers = useBrainStore.getState().customers;
    const pending = pickDeposit(
      p,
      (d) =>
        d.status === 'pending' &&
        (!!slotBookingId(d) || !!d.discussedSlot) &&
        !!(customers[d.customerId]?.email ?? '').trim(),
    );
    if (!pending) return false;
    simulateStripeWebhookPaid({ depositId: pending.id });
    return true;
  },

  // DEPOSITS — mint a fresh link for a customer who has none on file.
  'deposit-generate-link': (p) => {
    const brain = useBrainStore.getState();
    const held = new Set(Object.values(brain.deposits).map((d) => d.customerId));
    const customer =
      Object.values(brain.customers).find((c) => !held.has(c.id)) ??
      Object.values(brain.customers)[0];
    if (!customer) return false;
    const studioId = customer.primaryStudioId;
    const artistId =
      str(p, 'artistId') ??
      Object.values(brain.artists).find((a) => a.studioId === studioId)?.id ??
      Object.keys(brain.artists)[0];
    if (!artistId) return false;
    // A deposit link always carries a date and an artist (diagram 03; 7g3y): the
    // console holds a half day tomorrow at 11am unless it is given a start.
    const startIso = str(p, 'startIso') ?? tomorrowAt11();
    generateDepositLink({
      customerId: customer.id,
      studioId,
      artistId,
      amountAud: num(p, 'amountAud') ?? 400,
      channel: 'sms',
      sessionLength: 'half-day',
      source: 'agent-initiated-chat',
      discussedSlot: { artistId, startIso, endIso: shiftIsoByMinutes(startIso, 240) },
    });
    return true;
  },

  // DEPOSITS — the client opens the link (the ledger row goes from sent to viewed).
  'deposit-link-viewed': (p) => {
    const pending = pickDeposit(p, (d) => d.status === 'pending' && !d.viewedAt);
    if (!pending) return false;
    simulateDepositLinkViewed(pending.id);
    return true;
  },

  // DEPOSITS — chase an unpaid link. String form: the SAME record gets a fresh
  // 48h window (the expired-link path with its routing is `queue-expired-resend`).
  'deposit-resend': (p) => {
    // Only a dated link (7g3y: a link always carries a date).
    const pending = pickDeposit(p, (d) => d.status === 'pending' && !!slotBookingId(d));
    if (!pending) return false;
    return resendDepositLink(pending.id) !== undefined;
  },

  // DEPOSITS — run the expiry sweep against the current clock: server-side in
  // the served build (tv-2na.10.11), the in-browser reconcileDepositHolds in the
  // store-fallback build (simClockFlow.runDayAdvanced picks).
  'deposit-sweep': () => {
    // The whole day-advanced bundle on the current clock (tv-2na.10.13).
    void runDayAdvanced();
    return true;
  },

  // DEPOSITS — the client no-shows and the money is forfeited.
  'deposit-forfeit': (p) => {
    const brain = useBrainStore.getState();
    const target = pickBooking(p, (b) => {
      const dep = b.depositId ? brain.deposits[b.depositId] : undefined;
      return dep?.status === 'paid';
    });
    if (!target) return false;
    return (
      forfeitDepositForNoShow(target.id, { by: 'Console', note: 'Console: no-show forfeit' }) !==
      undefined
    );
  },

  // DEPOSITS — management gives a forfeited deposit back.
  'deposit-reinstate': (p) => {
    const forfeited = pickDeposit(p, (d) => d.ledger?.some((e) => e.kind === 'forfeit') ?? false);
    if (!forfeited) return false;
    // B4 (75ng): through the server's reinstate (Ops / owner, a reason on file).
    void reinstateDepositWithReason(forfeited.id, 'Console: reinstated');
    return true;
  },

  // DEPOSITS — money taken at the desk rather than through the link. A booking not
  // paid for yet goes through ONE atomic intent (it ends firm with its deposit
  // linked — tv-2na.8.11); the ledger names the signed-in person, not the surface.
  'deposit-manual-payment': (p) => {
    // D05-11: money taken at the desk needs the client's email on file.
    const customers = useBrainStore.getState().customers;
    const target = pickBooking(p, (b) => b.status !== 'cancelled' && !!(customers[b.customerId]?.email ?? '').trim());
    if (!target) return false;
    recordManualPayment({
      bookingId: target.id,
      amountAud: num(p, 'amountAud') ?? 200,
      method: 'eftpos',
      by: currentActorId() ?? 'Console',
    });
    return true;
  },

  // RECONCILE — park the first exception awaiting approval. The demo day itself
  // reconciles clean, so an unqualified command scans the studio's recent
  // reconciliation days for one that actually has an exception to work rather
  // than no-opping on today.
  'recon-inject-orphan': (p) => {
    const studioId = resolveStudio(p);
    if (!studioId) return false;
    const brain = useBrainStore.getState();
    // One new reference per click, with no sale counterpart; the normal
    // persistence bridge and reconciliation screen handle it from here.
    let n = 1;
    const prefix = `DEMO-ORPHAN-${studioId}-${today()}-`;
    const refs = new Set(Object.values(brain.docketLines).map(d => d.reference));
    while (refs.has(`${prefix}${n}`)) n += 1;
    const reference = `${prefix}${n}`;
    brain.upsertDocketLine({ id: reference, studioId, dayIso: today(), reference, amountAud: 200, source: 'terminal', name: 'Demo unmatched payment' });
    return true;
  },
  'recon-resolve': (p) => {
    const studioId = resolveStudio(p);
    if (!studioId) return false;
    // W7 n3: only Accounts resolves (the maker) — same gate as the screen.
    const persona = useSessionStore.getState().persona;
    if (!persona || !canResolveReconException(persona)) {
      return refuse('Only Accounts can resolve a reconciliation exception — switch the app to Robin Ashby (Accounts).');
    }
    const named = str(p, 'dayIso');
    const days = named ? [named] : [today(), ...reconciliationDaysFor(studioId)];
    let row: { id: string; reference: string } | undefined = str(p, 'lane') === 'orphan'
      ? orphanLane(studioId).find(r => (!named || r.dayIso === named) && !useBrainStore.getState().reconResolutions[r.id])
      : undefined;
    for (const day of str(p, 'lane') === 'orphan' ? [] : days) {
      row = reconcileDay(studioId, day).exceptions[0];
      if (row) break;
    }
    if (!row) return false;
    return (
      resolveReconRow({
        rowId: row.id,
        lane: 'makesale',
        reference: row.reference,
        note: 'Console: matched against the terminal batch.',
      }) !== undefined
    );
  },

  // RECONCILE — approve a parked row. The sign-off keys to the Finance Manager
  // (tv-vhs, maker-checker), and self-approval is blocked, so this only lands
  // when the app tab is on the Finance Manager and they did not resolve the row.
  'recon-approve': () => {
    const brain = useBrainStore.getState();
    const parked = Object.values(brain.reconResolutions).find(
      (r) => r.status === 'suggested-resolved',
    );
    if (!parked) return false;
    const result = approveReconResolution(parked.rowId);
    if (!result.ok) {
      return refuse(
        result.reason === 'self-approval'
          ? 'Self-approval is blocked — switch the app to the Finance Manager to sign this off.'
          : 'Approval refused: only the Finance Manager can sign off a reconciliation resolution.',
      );
    }
    return true;
  },

  // RECONCILE — bounce a parked row back to Needs you with a reason. Only the
  // Finance Manager (the checker) rejects (W7 n3).
  'recon-reject': () => {
    const brain = useBrainStore.getState();
    const parked = Object.values(brain.reconResolutions).find(
      (r) => r.status === 'suggested-resolved',
    );
    if (!parked) return false;
    const result = rejectReconResolution(parked.rowId, 'Console: the reference does not match the batch.');
    if (!result.ok && result.reason === 'no-clearance') {
      return refuse('Only the Finance Manager can bounce a resolution — switch the app to Dana Whitlock.');
    }
    return result.ok;
  },

  // RECONCILE — the simulated bank lands today's card batch now (W7 n1). The demo
  // clock is frozen, so the next business day never comes on its own; this echoes
  // the live checkouts' batch (without closing the day) and lands it today.
  // Aimed at a studio, that studio; un-aimed, every studio (whichever has a batch).
  'recon-land-batches': (p) => {
    const named = str(p, 'studioId') as StudioId | undefined;
    const studioIds = named ? [named] : (Object.keys(useBrainStore.getState().studios) as StudioId[]);
    const landed = studioIds.reduce((n, id) => n + landPendingBatches(id), 0);
    return landed > 0 || refuse('No pending card batches — check out a card session first.');
  },

  // CONFIRMATIONS — put a call on the worklist for the next upcoming booking.
  'confirm-ensure-call': (p) => {
    const nowMs = new Date(nowIso()).getTime();
    const target = pickBooking(
      p,
      (b) => b.status === 'confirmed' && new Date(b.start).getTime() >= nowMs,
    );
    if (!target) return false;
    return ensureConfirmationCall(target.id) !== '';
  },

  // CONFIRMATIONS — resolve the first open call (confirmed unless told otherwise).
  'confirm-outcome': (p) => {
    const brain = useBrainStore.getState();
    const aimedBookingId = str(p, 'bookingId');
    const calls = Object.values(brain.confirmationCalls);
    const call = aimedBookingId
      ? calls.find((c) => c.bookingId === aimedBookingId)
      : calls.find((c) => !c.outcome);
    if (!call) return false;
    const outcome = (str(p, 'outcome') ?? 'confirmed') as ConfirmationOutcome;
    return resolveConfirmationOutcome(call.id, outcome, {
      by: 'Console',
      ...(outcome === 'cancelled' ? { reason: str(p, 'reason') ?? 'Console: client cancelled on the confirmation call' } : {}),
    }).ok;
  },

  // CONFIRMATIONS — escalation chain: Ops asks the artist to reach out to a
  // client who has not confirmed after the cadence escalated (vault-06).
  'confirm-ask-artist': (p) => {
    const target = pickBooking(p, (b) => isConfirmationEscalated(b));
    if (!target) return refuse('No escalated booking to hand to the artist.');
    return askArtistToConfirm(target.id, { byPersonId: 'Console' }).ok;
  },

  // WAIVERS — send the waiver for the next booking that has none signed.
  'waiver-send': (p) => {
    const target = pickBooking(p, (b) => b.status !== 'cancelled');
    if (!target) return false;
    return sendWaiver({ bookingId: target.id }) !== undefined;
  },

  // WAIVERS — the client signs the outstanding waiver on their phone. When a
  // booking is aimed this MUST sign that booking's waiver: signing whichever
  // one happens to be first leaves the aimed check-in still blocked, with the
  // console reporting success.
  'waiver-signed': (p) => {
    const brain = useBrainStore.getState();
    const aimedBookingId = str(p, 'bookingId');
    const target = aimedBookingId
      ? latestWaiverForBooking(aimedBookingId)
      : Object.values(brain.waivers).find((w) => w.status === 'sent');
    if (!target) return refuse('No waiver to sign — send one first.');
    if (target.status === 'signed') return true; // already done, not a failure
    return simulateWaiverSigned(target.id) !== undefined;
  },

  // CALLS — an inbound call rings the workspace.
  'call-inbound': (p) => {
    const customerId = pickCustomerId(p);
    // L5 (D05-05): with no studio aimed, the client rings THEIR studio (it used
    // to be the first studio in the list, so a Coburg client rang "Brisbane").
    const studioId =
      (str(p, 'studioId') as StudioId | undefined) ??
      (customerId ? useBrainStore.getState().customers[customerId]?.primaryStudioId : undefined) ??
      resolveStudio(p);
    if (!customerId || !studioId) return false;
    dispatchInboundCall({ customerId, studioId });
    return true;
  },

  // SWEEPER (B7) — the Sweeper rang the aimed client about a gap today and got no
  // answer: the client's callback now routes to whoever is signed in (7 days).
  'sweeper-no-answer': (p) => {
    const customerId = pickCustomerId(p);
    const artistId = pickArtistId(p);
    if (!customerId || !artistId) return false;
    return recordSweeperOutcome({ customerId, artistId, dayIso: nowIso(), outcome: 'no-answer' })
      ? true
      : refuse('Sign in as the Sweeper first.');
  },

  // CALLS — the operator answers whatever is ringing (the transcript starts).
  'call-answer': () => {
    const call = getActiveCall();
    if (!call) return false;
    answerInboundCall(call.id);
    return true;
  },

  // CALLS — the operator declines it.
  'call-decline': () => {
    const call = getActiveCall();
    if (!call) return false;
    declineInboundCall(call.id);
    return true;
  },

  // CALLS — the studio rings a client.
  'call-outbound': (p) => {
    const customerId = pickCustomerId(p);
    const studioId = resolveStudio(p);
    if (!customerId || !studioId) return false;
    startOutboundCall({ customerId, studioId });
    return true;
  },

  // CALLS — wrap up whatever call is live, with a disposition.
  'call-end': (p) => {
    if (!getActiveCall()) return false;
    endCall({ tags: [str(p, 'tag') ?? 'deposit-sent'], notes: 'Console: call wrapped up.' });
    return true;
  },

  // QUEUE — log an outbound attempt that did not connect (arms the 24h window).
  'queue-contact-attempt': (p) => {
    const customerId = pickCustomerId(p);
    if (!customerId) return false;
    recordContactAttempt(customerId);
    return true;
  },

  // DEPOSITS — chase the balance owing on a part-paid deposit.
  'deposit-topup-reminder': (p) => {
    const target = pickDeposit(p, (d) => depositPaidAud(d) > 0 && depositPaidAud(d) < d.amountAud);
    if (!target) return false;
    sendTopupReminder(target.id);
    return true;
  },

  // DEPOSITS — run the top-up instalment reconciler against the current clock:
  // fires each due reminder and marks a miss once a reminded due day has passed.
  'topup-reconcile': () => {
    // Through the day-advanced bundle: the server owns the schedule in the served
    // build (tv-2na.10.13); the store-fallback build runs reconcileTopupSchedule.
    void runDayAdvanced();
    return true;
  },

  // QUEUE — run the self-book link expiry reconciler against the current clock:
  // a passed named link is marked expired (read-only artist notification) and
  // its follow-up is minted as an Agent hot lead, NOT a Sweeper gap item (tv-311).
  'selfbook-expiry-reconcile': () => {
    // B5: the server's day advance owns self-book expiry; the browser reconciler
    // is the store-fallback mirror only.
    if (isStoreFallbackEnabled()) reconcileSelfBookLinkExpiry();
    else void runDayAdvanced();
    return true;
  },

  // QUEUE — a locked date was taken first (this-week overbook, tv-6d8). Finds the
  // first held booking whose slot a live booking now overlaps and re-calls that
  // client onto the Agent worklist (warm, build-the-booking), never the Sweeper.
  'overbook-recall': () => {
    const brain = useBrainStore.getState();
    for (const b of Object.values(brain.bookings)) {
      if (b.status !== 'held') continue;
      if (routeOverbookRecall(b.id)?.outcome === 'recalled') return true;
    }
    return false;
  },

  // DEPOSITS — a missed top-up chase sequence goes unanswered. Increments the
  // chase counter on the first active plan with a missed instalment; the 3rd
  // unanswered sequence cancels the plan and runs the 7-day split (forfeit inside
  // 7 days, keep the deposit on file outside). Domain logic Wave 2 (tv-9vr)
  // surfaces into the queue; the console drives it for the demo.
  'topup-chase-no-answer': (p) => {
    const brain = useBrainStore.getState();
    const aimed = str(p, 'planId');
    const plan = aimed
      ? brain.topupPlans[aimed]
      : Object.values(brain.topupPlans).find(
          (pl) => pl.status === 'active' && pl.instalments.some((i) => i.status === 'missed'),
        );
    if (!plan) return false;
    return topupChaseNoAnswer(plan.id, { by: 'Console' }).ok;
  },

  // NURTURE — a re-engaged client replies / calls back on a nurture touch. Routes
  // to their studio agent onto the Alerts tab with a "why" summary. Prefers a
  // client already nurtured; else recycles a red first so the beat always fires.
  'nurture-inbound': (p) => {
    const kind: 'reply' | 'callback' = str(p, 'kind') === 'callback' ? 'callback' : 'reply';
    const latest = () =>
      Object.values(useBrainStore.getState().nurtureTouches).sort((a, b) =>
        b.sentAt.localeCompare(a.sentAt),
      )[0];
    let touch = latest();
    if (!touch) {
      reconcileNurture();
      touch = latest();
    }
    if (!touch) return false;
    return nurtureInbound(
      touch.customerId,
      kind,
      kind === 'reply' ? { text: 'Yeah keen — when can I come in?' } : {},
    ).ok;
  },

  // DEPOSITS — build a pay-over-time plan on a deposit that has none. Picks the
  // first deposit with a positive amount and no plan yet, splits it into a
  // sensible fortnightly cadence (>= the $50 floor) starting today, and mints the
  // plan through the same builder the deposit-send modal uses.
  'topup-plan-create': (p) => {
    const brain = useBrainStore.getState();
    const target = pickDeposit(
      p,
      (d) => d.amountAud >= MIN_INSTALMENT_AUD && !brain.topupPlans[`topup-${d.id}`],
    );
    if (!target) return false;
    const instalmentAud = Math.max(MIN_INSTALMENT_AUD, Math.round(target.amountAud / 4));
    return createTopupPlan({
      depositId: target.id,
      startIso: nowIso(),
      frequency: 'fortnightly',
      instalmentAud,
    }).ok;
  },

  // DEPOSITS — the customer taps to pay the next instalment on an active plan.
  // Mirrors the hosted tap-to-pay page's confirm; aim with `planId`, else the
  // first active plan with an unpaid instalment.
  'topup-instalment-pay': (p) => {
    const brain = useBrainStore.getState();
    const aimed = str(p, 'planId');
    const plan = aimed
      ? brain.topupPlans[aimed]
      : Object.values(brain.topupPlans).find(
          (pl) =>
            pl.status === 'active' &&
            // D05-11: money is only taken with the client's email on file.
            !!(brain.customers[pl.customerId]?.email ?? '').trim() &&
            pl.instalments.some(
              (i) => i.status === 'scheduled' || i.status === 'reminded' || i.status === 'missed',
            ),
        );
    if (!plan) return false;
    return payTopupInstalment(plan.id, { by: 'Console' }).ok;
  },

  // CALENDAR — management waives the deposit outright (no request, no queue).
  'calendar-waive-deposit': (p) => {
    const target = pickBooking(p, (b) => !b.depositId);
    if (!target) return false;
    return (
      waiveDepositDirectly({
        bookingId: target.id,
        by: 'Console',
        note: 'Console: waived on the spot',
      }) !== undefined
    );
  },

  // ARTIST — the artist writes up the session from the chair.
  'artist-session-note': (p) => {
    const target = pickBooking(p, (b) => b.status === 'in-chair' || b.status === 'completed');
    if (!target) return false;
    return (
      recordArtistSessionNote(target.id, 'Console: outlined and started shading, healing well.') !==
      undefined
    );
  },

  // ARTIST — progress photos land on the piece from the artist's phone.
  'artist-progress-photo': (p) => {
    const target = pickBooking(p, (b) => !!b.pieceId && b.status !== 'cancelled');
    if (!target) return false;
    recordArtistProgressPhotos(target.id, [{ url: PROGRESS_PHOTO_URL, kind: 'progress' }]);
    return true;
  },

  // LINKS — the client completes a universal self-book link end to end.
  'selfbook-complete': (p) => {
    const brain = useBrainStore.getState();
    const artistId = pickArtistId(p);
    const artist = artistId ? brain.artists[artistId] : undefined;
    if (!artist) return false;
    const link = getOrCreateUniversalLink({ artistId: artist.id, studioId: artist.studioId });
    const customerId = pickCustomerId(p);
    const customer = customerId ? brain.customers[customerId] : undefined;
    if (!customer) return false;
    const result = completeUniversalSelfBook({
      link,
      session: 'half-day',
      startIso: tomorrowAt11(),
      details: {
        name: customerName(customer),
        email: customer.email ?? 'client@example.com',
        mobile: customer.phone ?? '0400 000 000',
      },
      designBrief: 'Console: booked through the artist link.',
      referencePhotos: [],
    });
    return Boolean(result.bookingId);
  },

  // LINKS — generate a self-book link for an artist to hand out.
  'selfbook-link': (p) => {
    const brain = useBrainStore.getState();
    const artistId = pickArtistId(p);
    const artist = artistId ? brain.artists[artistId] : undefined;
    if (!artist) return false;
    const fortnightOut = shiftIsoByMinutes(nowIso(), 14 * 24 * 60);
    generateSelfBookLink({
      artistId: artist.id,
      studioId: artist.studioId,
      channelSent: 'sms',
      dateRangeStart: today(),
      dateRangeEnd: studioDayKey(fortnightOut),
      expiresAt: fortnightOut,
    });
    return true;
  },
};

const GLOBAL_ACTIONS = new Set([
  'global:reset',
  'global:play',
  'global:set-clock',
  'global:beat',
  'global:rollover',
  'global:nav',
]);

/** True when the dispatcher can actually execute this action. Lets the catalogue
 *  be checked against the dispatcher in a test, so a mistyped card fails the
 *  build instead of silently doing nothing on the night. */
export function isKnownControlAction(action: string): boolean {
  if (GLOBAL_ACTIONS.has(action)) return true;
  if (action.startsWith('sim-event:')) return action.length > 'sim-event:'.length;
  if (action.startsWith('flow:')) return action.slice('flow:'.length) in FLOW_HANDLERS;
  return false;
}

export function dispatchControlCommand(action: string, payload?: Payload): ControlResult {
  lastNote = undefined;
  // Display-only: the bridge has already navigated, there is nothing to execute.
  if (action === 'global:nav') return { ok: true };
  // Global controls.
  if (action === 'global:reset') {
    simulationApi.reset();
    return { ok: true };
  }
  if (action === 'global:play') {
    simulationApi.play();
    return { ok: true };
  }
  if (action === 'global:set-clock') {
    const iso = str(payload, 'iso');
    if (!iso) return { ok: false, note: 'No time given.' };
    // Decided 23 Sep: a card's time of day applies to the CURRENT sim day.
    return moveSimClock(timeOnSimDay(nowIso(), iso));
  }
  // Fire one named beat from the script (by id, so a beat can be re-fired out of
  // order — `global:play` walks them in sequence instead).
  if (action === 'global:beat') {
    const eventId = str(payload, 'eventId');
    if (!eventId) return { ok: false, note: 'No beat chosen.' };
    simulationApi.triggerEvent(eventId);
    return { ok: true };
  }
  if (action === 'global:rollover') {
    const iso = str(payload, 'iso');
    if (!iso) return { ok: false, note: 'No rollover time given.' };
    // A rollover names the new day itself, so it moves to that instant as given.
    const result = moveSimClock(iso);
    runDailyAlertPass(iso); // fire the day's threshold alerts (play() does this on a day change)
    return result;
  }

  // Sim-event pass-through: action is "sim-event:<type>". Prefer the SCRIPTED
  // beat of that type — it carries the authored payload and description, and
  // firing it by id is what gets recorded in `fired()`, so the console can grey
  // the card out. Types with no scripted beat (the console-era additions) fall
  // back to a synthetic trigger.
  if (action.startsWith('sim-event:')) {
    const type = action.slice('sim-event:'.length) as SimEventType;
    const scripted = SCRIPTED_EVENTS.find((e) => e.type === type);
    if (scripted) simulationApi.triggerEvent(scripted.id);
    else simulationApi.triggerEventByType(type);
    return { ok: true };
  }

  // Flow command: action is "flow:<name>".
  if (action.startsWith('flow:')) {
    const name = action.slice('flow:'.length);
    const handler = FLOW_HANDLERS[name];
    if (!handler) return { ok: false, note: `Unknown trigger "${name}".` };
    const acted = handler(payload);
    if (acted) return { ok: true };
    return { ok: false, note: lastNote ?? 'Nothing in the current state to act on.' };
  }

  return { ok: false, note: `Unknown command "${action}".` };
}
